import { BanknotesIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { ArrowLeft } from 'lucide-react';
import React, { useState } from 'react';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { toast } from 'sonner';

import { ChequeDetails } from '@/components/postgrid/cheque-details';
import { ChequeWizard } from '@/components/postgrid/cheque-wizard/ChequeWizard';
import { LetterWizard } from '@/components/postgrid/letter-wizard/LetterWizard';
import { LetterDetails } from '@/components/postgrid/postgrid-letters/letter-details';
import { MailableTable } from '@/components/postgrid/postgrid-mailables/mailable-table/MailableTable';
import { Button } from '@/components/ui/button';
import {
  useCreatePostgridChequeMutation,
  useCreatePostgridLetterMutation,
} from '@/services/api/postgridService';

import useMatterSelection from '../../hooks/useMatterSelection';

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

interface MailHomeProps {
  matterId: string;
  onNewCheque: () => void;
  onNewLetter: () => void;
  onViewDetails: (id: string, type: 'cheque' | 'letter') => void;
}

const MailHome: React.FC<MailHomeProps> = ({
  matterId,
  onNewCheque,
  onNewLetter,
  onViewDetails,
}) => (
  // Follow TaskListView pattern: flex container with explicit height
  <div
    className="flex flex-col gap-4 overflow-hidden bg-zinc-50 px-6 py-4 dark:bg-zinc-950"
    style={{ height: 'calc(100vh - 120px)', contain: 'layout style' }}
  >
    {/* Header - flex-shrink-0 to prevent compression */}
    <div className="flex flex-shrink-0 items-center justify-between">
      <div>
        <h1 className="text-2xl font-semibold text-zinc-900 dark:text-white">
          Mail
        </h1>
        <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
          Send letters and payments for this case
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Button
          onClick={onNewLetter}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <PaperAirplaneIcon className="h-4 w-4 mr-2" /> Send Mail
        </Button>
        <Button onClick={onNewCheque} variant="outline">
          <BanknotesIcon className="h-4 w-4 mr-2" /> Send Check
        </Button>
      </div>
    </div>

    {/* Table - flex-1 to fill remaining space */}
    <MailableTable matterId={matterId} onDetailsClick={onViewDetails} />
  </div>
);

/* -------------------------------------------------------------------------- */
/*                                 Breadcrumb                                 */
/* -------------------------------------------------------------------------- */

const Breadcrumb: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <Button variant="ghost" onClick={onBack} className="gap-2">
    <ArrowLeft className="h-4 w-4" /> Back to Mail
  </Button>
);

/* -------------------------------------------------------------------------- */
/*                            Cheque Wizard Wrapper                           */
/* -------------------------------------------------------------------------- */

const ChequeWizardPage: React.FC<{ matterId: string }> = ({ matterId }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [createCheque, { isLoading }] = useCreatePostgridChequeMutation();

  // Extract query parameters for prefilling
  const prefilledToContactId = searchParams.get('to') || undefined;
  const prefilledAmount = searchParams.get('amount') || undefined;
  const prefilledMemo = searchParams.get('memo') || undefined;

  const handleCreate = async (
    payload: Parameters<typeof createCheque>[0]['createPostgridCheque'],
  ) => {
    try {
      await createCheque({ createPostgridCheque: payload }).unwrap();
      toast.success('Cheque created and queued for mailing');
      navigate('../');
    } catch {
      toast.error('Failed to create cheque');
    }
  };

  const handleCancel = () => navigate('../');

  return (
    <div className="p-6 space-y-6">
      <Breadcrumb onBack={handleCancel} />
      <ChequeWizard
        matterId={matterId}
        onSuccess={handleCreate}
        onCancel={handleCancel}
        isLoading={isLoading}
        prefilledToContactId={prefilledToContactId}
        prefilledAmount={prefilledAmount}
        prefilledMemo={prefilledMemo}
      />
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                            Letter Wizard Wrapper                           */
/* -------------------------------------------------------------------------- */

const LetterWizardPage: React.FC<{ matterId: string }> = ({ matterId }) => {
  const navigate = useNavigate();
  const [createLetter, { isLoading }] = useCreatePostgridLetterMutation();

  const handleCreate = async (
    payload: Parameters<typeof createLetter>[0]['createPostgridLetter'],
  ) => {
    try {
      await createLetter({ createPostgridLetter: payload }).unwrap();
      toast.success('Letter created and queued for mailing');
      navigate('../');
    } catch {
      toast.error('Failed to create letter');
    }
  };

  const handleCancel = () => navigate('../');

  return (
    <div className="p-6 space-y-6">
      <Breadcrumb onBack={handleCancel} />
      <LetterWizard
        matterId={matterId}
        onSuccess={handleCreate}
        onCancel={handleCancel}
        isLoading={isLoading}
      />
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                              Main Tab Export                               */
/* -------------------------------------------------------------------------- */

const MatterMailTab: React.FC = () => {
  const { selectedMatterData } = useMatterSelection();
  const location = useLocation();
  const navigate = useNavigate();

  // Detail view state
  const [selectedMailableId, setSelectedMailableId] = useState<string | null>(
    null,
  );
  const [selectedMailableType, setSelectedMailableType] = useState<
    'cheque' | 'letter' | null
  >(null);

  if (!selectedMatterData?.id) {
    return <div className="p-6">Loading matter data...</div>;
  }

  const matterId = selectedMatterData.id;

  const handleViewDetails = (id: string, type: 'cheque' | 'letter') => {
    setSelectedMailableId(id);
    setSelectedMailableType(type);
  };

  const handleCloseDetails = () => {
    setSelectedMailableId(null);
    setSelectedMailableType(null);
  };

  return (
    <>
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <MailHome
              matterId={matterId}
              onNewCheque={() => navigate('cheque-wizard')}
              onNewLetter={() => navigate('letter-wizard')}
              onViewDetails={handleViewDetails}
            />
          }
        />
        <Route
          path="cheque-wizard"
          element={<ChequeWizardPage matterId={matterId} />}
        />
        <Route
          path="letter-wizard"
          element={<LetterWizardPage matterId={matterId} />}
        />
      </Routes>

      {/* Detail Drawers */}
      {selectedMailableId && selectedMailableType === 'cheque' && (
        <ChequeDetails
          chequeId={selectedMailableId}
          open
          onOpenChange={handleCloseDetails}
        />
      )}

      {selectedMailableId && selectedMailableType === 'letter' && (
        <LetterDetails
          letterId={selectedMailableId}
          open
          onOpenChange={handleCloseDetails}
        />
      )}
    </>
  );
};

export default MatterMailTab;
